import { useCallback, useEffect, useRef, useState } from 'react';
import { getBulkProgress, BulkProgressState } from '@/shared/actions/erp/tickets-soporte-cliente';

interface UseTicketBulkProgressOptions {
  operacionId: string | null;
  interval?: number;
  onComplete?: () => void;
}

export default function useTicketBulkProgress({
  operacionId,
  interval = 1500,
  onComplete,
}: UseTicketBulkProgressOptions) {
  const [progress, setProgress] = useState<BulkProgressState | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    setProgress(null);
  }, [stopPolling]);

  useEffect(() => {
    if (!operacionId) {
      stopPolling();
      return;
    }

    setIsPolling(true);

    const poll = async () => {
      try {
        const res = await getBulkProgress(operacionId);
        const data = (res as any)?.data?.data ?? (res as any)?.data;
        if (data) {
          setProgress(data);
          if (data.estado === 'COMPLETADO' || data.estado === 'ERROR') {
            stopPolling();
            onCompleteRef.current?.();
          }
        }
      } catch {
        // 404 means operation expired or not found
        stopPolling();
      }
    };

    poll(); // immediate first poll
    timerRef.current = setInterval(poll, interval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [operacionId, interval, stopPolling]);

  return { progress, isPolling, reset };
}
